import React, { useEffect, useState, useRef } from 'react';
import { AssetWrapperProps } from 'src/app/atoms/Asset/AssetWrapper';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import TabbedFeaturesAssetCarousel from './TabbedFeaturesAssetCarousel';
import TabbedFeaturesSelector from './TabbedFeaturesSelector';

export interface TabbedFeaturesProps {
  tabs: {
    label: string;
    videoProps?: AssetWrapperProps['videoProps'];
    imageProps?: AssetWrapperProps['imageProps'];
  }[];
  initialActiveIndex?: number;
  autoChangeTimeInMilliseconds?: number;
}

const TabbedFeatures = ({
  tabs,
  initialActiveIndex = 0,
  autoChangeTimeInMilliseconds,
}: TabbedFeaturesProps): JSX.Element => {
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const [disabled, setDisabled] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout>();

  const clearAutoChangeInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  };

  const startInterval = () => {
    if (autoChangeTimeInMilliseconds) {
      intervalRef.current = setInterval(() => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % tabs.length);
      }, autoChangeTimeInMilliseconds);
    }
  };

  useEffect(() => {
    startInterval();

    return clearAutoChangeInterval;
  }, [autoChangeTimeInMilliseconds]);

  const handleTabClick = (index: number) => {
    if (disabled) return;

    setDisabled(true);
    setActiveIndex(index);

    clearAutoChangeInterval();
    startInterval();
  };

  const handleMoveToTabEnd = () => setDisabled(false);

  const assets = tabs.map((tab, index) => ({
    ...tab,
    active: activeIndex === index,
  }));

  return (
    <Stack
      gap='spacing-6xl'
      padding={['spacing-none', 'spacing-4xl']}
      direction='column'
      maxWidth='width-3xl'
      paddingSm='spacing-none'
      gapSm='spacing-4xl'
    >
      <TabbedFeaturesAssetCarousel
        assets={assets}
        onMoveEnd={handleMoveToTabEnd}
      />
      <TabbedFeaturesSelector
        tabs={assets}
        onTabClick={handleTabClick}
      />
    </Stack>
  );
};

export default TabbedFeatures;
